import { useState, useEffect, useCallback } from 'react';
import { collection, query, getDocs } from 'firebase/firestore';
import { db } from '../lib/firebase';

interface Metrics {
  totalPoints: number;
  averagePoints: number;
  activeStudents: number;
  openingPoints: number;
  clickPoints: number;
  engagementPoints: number;
}

export function useMetrics() {
  const [metrics, setMetrics] = useState<Metrics>({
    totalPoints: 0,
    averagePoints: 0,
    activeStudents: 0,
    openingPoints: 0,
    clickPoints: 0,
    engagementPoints: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchMetrics = useCallback(async () => {
    try {
      setLoading(true);

      const q = query(collection(db, 'students'));
      const snapshot = await getDocs(q);
      
      const students = snapshot.docs.map(doc => doc.data());
      
      const activeStudents = students.filter(student => 
        student.interaction_points > 0
      ).length;

      const totalPoints = students.reduce((sum, student) => 
        sum + (student.interaction_points || 0), 0
      );

      const openingPoints = students.reduce((sum, student) => 
        sum + (student.openings_points || 0), 0
      );

      const clickPoints = students.reduce((sum, student) => 
        sum + (student.clicks_points || 0), 0
      );

      setMetrics({
        totalPoints,
        averagePoints: activeStudents ? Math.round(totalPoints / activeStudents) : 0,
        activeStudents,
        openingPoints,
        clickPoints,
        engagementPoints: totalPoints - (openingPoints + clickPoints),
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMetrics();
  }, [fetchMetrics]);

  return { metrics, loading, error, refresh: fetchMetrics };
}